/**
 * STICKER AI ENGINE V5.1 - AVISOCAR
 * ==================================
 * Smart Variant Generator (Token-Based, NOT Image Generator)
 * 
 * This engine generates DESIGN TOKENS, not images.
 * Your existing Sticker Builder renders them.
 * 
 * Features:
 * - 5 emotional variants per request
 * - Peru/LATAM optimized slogans
 * - Emotional trigger words
 * - Brand consistency maintained
 * - QR position NEVER changes
 * 
 * Input: category, tone, country, urgency
 * Output: Array of 5 design token objects
 */

// ============================================
// EMOTIONAL TRIGGER WORDS (PERU/LATAM)
// ============================================
const EMOTIONAL_WORDS = {
  urgente: ["rápido", "urgente", "ahora", "inmediato", "alerta", "avisa"],
  familiar: ["familia", "hogar", "cariño", "protegido", "querido", "seguro"],
  divertido: ["ups", "oops", "hey", "hola", "amigo", "ayuda"],
  premium: ["exclusivo", "privado", "discreto", "elegante", "profesional"],
  seguridad: ["anónimo", "seguro", "privado", "protegido", "confidencial"],
};

// ============================================
// COLOR PALETTES BY TONE
// ============================================
const TONE_PALETTES = {
  urgente: [
    { primary: "#DC2626", secondary: "#FCA5A5", accent: "#FFFFFF", name: "Alerta Rojo" },
    { primary: "#F97316", secondary: "#FDBA74", accent: "#FFFFFF", name: "Naranja Urgente" },
    { primary: "#EAB308", secondary: "#FDE047", accent: "#1F2937", name: "Amarillo Alerta" },
    { primary: "#0EA5E9", secondary: "#7DD3FC", accent: "#FFFFFF", name: "Azul Acción" },
    { primary: "#8B5CF6", secondary: "#C4B5FD", accent: "#FFFFFF", name: "Violeta Impacto" },
  ],
  divertido: [
    { primary: "#EC4899", secondary: "#F9A8D4", accent: "#FFFFFF", name: "Rosa Alegre" },
    { primary: "#10B981", secondary: "#6EE7B7", accent: "#FFFFFF", name: "Verde Amigable" },
    { primary: "#06B6D4", secondary: "#67E8F9", accent: "#FFFFFF", name: "Cyan Fresco" },
    { primary: "#F59E0B", secondary: "#FCD34D", accent: "#1F2937", name: "Dorado Feliz" },
    { primary: "#8B5CF6", secondary: "#DDD6FE", accent: "#FFFFFF", name: "Morado Fun" },
  ],
  familiar: [
    { primary: "#10B981", secondary: "#A7F3D0", accent: "#FFFFFF", name: "Verde Hogar" },
    { primary: "#3B82F6", secondary: "#93C5FD", accent: "#FFFFFF", name: "Azul Familia" },
    { primary: "#F97316", secondary: "#FED7AA", accent: "#FFFFFF", name: "Naranja Cálido" },
    { primary: "#EC4899", secondary: "#FBCFE8", accent: "#FFFFFF", name: "Rosa Cariño" },
    { primary: "#14B8A6", secondary: "#99F6E4", accent: "#FFFFFF", name: "Teal Confianza" },
  ],
  premium: [
    { primary: "#1F2937", secondary: "#4B5563", accent: "#F59E0B", name: "Carbón Dorado" },
    { primary: "#0F172A", secondary: "#334155", accent: "#FFFFFF", name: "Midnight Pro" },
    { primary: "#7C3AED", secondary: "#A78BFA", accent: "#FFFFFF", name: "Violeta Royal" },
    { primary: "#0369A1", secondary: "#38BDF8", accent: "#FFFFFF", name: "Azul Ejecutivo" },
    { primary: "#166534", secondary: "#22C55E", accent: "#FFFFFF", name: "Verde Élite" },
  ],
};

// ============================================
// SLOGAN TEMPLATES BY CATEGORY & TONE
// ============================================
const SLOGAN_TEMPLATES = {
  vehiculos: {
    urgente: [
      "¡Avisa rápido! Tu auto bloquea",
      "¿Bloqueo tu paso? Escanea ya",
      "Alerta: necesito que me muevan",
      "¡Urgente! Contacta al dueño",
      "No pierdas tiempo, escanea aquí",
    ],
    divertido: [
      "¡Ups! Creo que te bloqueo",
      "Oops, ¿molesto? Avísame",
      "Hey! Si estorbo, escanea",
      "¡Hola! ¿Te bloqueo? Avisa",
      "¿Mal estacionado? Sorry!",
    ],
    familiar: [
      "Gracias por avisar con cariño",
      "Tu ayuda protege mi auto",
      "Familia tranquila, tú avisas",
      "Cuida a los demás, avisa",
      "Juntos cuidamos nuestros autos",
    ],
    premium: [
      "Contacto discreto y seguro",
      "Comunicación anónima premium",
      "Servicio exclusivo AvisoCar",
      "Tu privacidad, nuestra prioridad",
      "Elegancia en cada contacto",
    ],
  },
  ninos: {
    urgente: [
      "¡Ayúdame a volver! Escanea ya",
      "Si me pierdo, avisa rápido",
      "¡Urgente! Ayuda a encontrarme",
      "Mis papás me buscan, avisa",
      "¡Alerta! Escanea para avisar",
    ],
    divertido: [
      "¡Soy explorador! Avisa si me ves",
      "¿Me perdí? ¡Ayúdame a volver!",
      "Soy pequeño y necesito ayuda",
      "¡Aventura! Pero avisa a mamá",
      "Si me encuentras, ¡gracias!",
    ],
    familiar: [
      "Mi familia me espera, avisa",
      "Estoy protegido con AvisoCar",
      "Mis papás te agradecerán",
      "Cuidamos juntos a los niños",
      "Tu ayuda me lleva a casa",
    ],
    premium: [
      "Protección infantil premium",
      "Seguridad discreta para niños",
      "Contacto seguro con la familia",
      "Tecnología que protege niños",
      "Privacidad y seguridad infantil",
    ],
  },
  mascotas: {
    urgente: [
      "¡Mi humano me busca! Avisa ya",
      "¿Me encontraste? Escanea rápido",
      "¡Urgente! Ayúdame a volver",
      "Estoy perdido, avisa ahora",
      "Mi familia me extraña, ayuda",
    ],
    divertido: [
      "¡Guau! ¿Me encontraste?",
      "Soy adorable pero estoy perdido",
      "¡Miau! Avisa a mi humano",
      "Patitas perdidas, corazón leal",
      "¿Me das una mano... o patita?",
    ],
    familiar: [
      "Soy parte de una familia",
      "Mi hogar me espera con amor",
      "Gracias por cuidarme",
      "Unidos por nuestras mascotas",
      "Tu bondad me lleva a casa",
    ],
    premium: [
      "Protección premium para mascotas",
      "Contacto discreto con el dueño",
      "Tecnología que cuida mascotas",
      "Servicio exclusivo AvisoCar",
      "Privacidad para tu mascota",
    ],
  },
  objetos: {
    urgente: [
      "¿Encontraste esto? Avisa rápido",
      "¡Urgente! Este objeto tiene dueño",
      "Alerta: objeto extraviado",
      "Ayuda a devolverlo ya",
      "¡Escanea y avisa al dueño!",
    ],
    divertido: [
      "¡Ups! Creo que me olvidaron",
      "Oops, alguien me dejó aquí",
      "¿Me llevas a casa? Escanea",
      "Soy importante para alguien",
      "¡Ayuda! Busco a mi dueño",
    ],
    familiar: [
      "Este objeto tiene historia",
      "Alguien lo extraña mucho",
      "Tu bondad hace la diferencia",
      "Gracias por ser honesto",
      "Unidos devolvemos lo perdido",
    ],
    premium: [
      "Devolución discreta y segura",
      "Contacto privado profesional",
      "Servicio premium de devolución",
      "Privacidad garantizada",
      "Elegancia en cada devolución",
    ],
  },
  viajes: {
    urgente: [
      "¡Equipaje urgente! Avisa ya",
      "Esta maleta tiene dueño",
      "¡Alerta! Equipaje extraviado",
      "Ayuda urgente con mi maleta",
      "Escanea y avisa rápido",
    ],
    divertido: [
      "¡Mi maleta se fue de vacaciones!",
      "Oops, ¿dónde está mi dueño?",
      "Soy una maleta aventurera",
      "¿Me llevas a mi vuelo?",
      "¡Esta maleta busca hogar!",
    ],
    familiar: [
      "Recuerdos de viaje adentro",
      "Mi familia necesita esto",
      "Gracias por tu honestidad",
      "Viajeros se ayudan entre sí",
      "Tu bondad viaja conmigo",
    ],
    premium: [
      "Devolución ejecutiva de equipaje",
      "Servicio premium para viajeros",
      "Contacto discreto garantizado",
      "Privacidad para tu equipaje",
      "Elegancia en tus viajes",
    ],
  },
  emergencias: {
    urgente: [
      "EN CASO DE EMERGENCIA escanea",
      "¡Información vital! Escanea ya",
      "Datos médicos urgentes aquí",
      "¡Alerta! Contacto de emergencia",
      "Si necesito ayuda, escanea",
    ],
    divertido: [
      "¡Esperemos no necesitarlo!",
      "Por si acaso, escanea aquí",
      "Info importante (pero positiva)",
      "Preparados para todo",
      "Mejor prevenir, ¿no?",
    ],
    familiar: [
      "Mi familia debe saber esto",
      "Información para quienes amo",
      "Cuida a los que más quiero",
      "Unidos en emergencias",
      "Tu ayuda salva familias",
    ],
    premium: [
      "Información médica confidencial",
      "Contacto de emergencia privado",
      "Servicio premium de emergencias",
      "Datos seguros y discretos",
      "Protección profesional total",
    ],
  },
  privacidad: {
    urgente: [
      "Contacto privado y rápido",
      "Avisa sin revelar tu identidad",
      "Contacto privado mediante AvisoCar",
      "Comunicación segura ahora",
      "Tu identidad está protegida",
    ],
    divertido: [
      "Shhh... avisa en secreto",
      "Tu identidad es un misterio",
      "Modo incógnito activado",
      "Privacidad con estilo",
      "Anonimato divertido",
    ],
    familiar: [
      "Protegemos tu privacidad",
      "Seguridad para toda la familia",
      "Confianza y discreción",
      "Tu identidad, nuestra prioridad",
      "Privacidad con cariño",
    ],
    premium: [
      "Máxima privacidad garantizada",
      "Anonimato de nivel ejecutivo",
      "Discreción profesional total",
      "Privacidad premium AvisoCar",
      "Elegancia en tu anonimato",
    ],
  },
};

// ============================================
// ICON RECOMMENDATIONS BY CATEGORY & TONE
// ============================================
const ICON_RECOMMENDATIONS = {
  vehiculos: {
    urgente: [["lights", "car", "alarm"], ["sos", "car", "warning"], ["car", "phone", "lights"]],
    divertido: [["car", "phone"], ["car", "lock"], ["car"]],
    familiar: [["car", "family", "lock"], ["car", "heart", "phone"], ["car", "home"]],
    premium: [["car", "shield", "lock"], ["car", "secure"], ["lock", "car"]],
  },
  ninos: {
    urgente: [["child", "sos", "phone"], ["child", "emergency", "family"], ["child", "lights"]],
    divertido: [["child", "heart"], ["child", "star"], ["child", "family"]],
    familiar: [["child", "family", "heart"], ["child", "home", "lock"], ["family", "child"]],
    premium: [["child", "shield", "lock"], ["child", "secure"], ["lock", "child"]],
  },
  mascotas: {
    urgente: [["paw", "sos", "phone"], ["paw", "emergency"], ["paw", "lights"]],
    divertido: [["paw", "heart"], ["paw", "bone"], ["paw"]],
    familiar: [["paw", "home", "heart"], ["paw", "collar", "lock"], ["paw", "family"]],
    premium: [["paw", "shield", "lock"], ["paw", "secure"], ["lock", "paw"]],
  },
  objetos: {
    urgente: [["luggage", "sos", "phone"], ["keys", "emergency"], ["luggage", "lights"]],
    divertido: [["luggage", "heart"], ["keys"], ["wallet"]],
    familiar: [["luggage", "home", "heart"], ["keys", "lock"], ["wallet", "family"]],
    premium: [["luggage", "shield", "lock"], ["keys", "secure"], ["lock", "wallet"]],
  },
  viajes: {
    urgente: [["plane", "sos", "phone"], ["luggage", "emergency"], ["ticket", "lights"]],
    divertido: [["plane", "heart"], ["luggage"], ["map"]],
    familiar: [["plane", "family", "heart"], ["luggage", "home"], ["ticket", "family"]],
    premium: [["plane", "shield", "lock"], ["luggage", "secure"], ["passport", "lock"]],
  },
  emergencias: {
    urgente: [["sos", "ambulance", "phone"], ["emergency", "fire"], ["sos", "police"]],
    divertido: [["sos", "heart"], ["ambulance"], ["sos"]],
    familiar: [["sos", "family", "heart"], ["ambulance", "home"], ["emergency", "family"]],
    premium: [["sos", "shield", "lock"], ["ambulance", "secure"], ["emergency", "lock"]],
  },
  privacidad: {
    urgente: [["lock", "shield", "phone"], ["anonymous", "secure"], ["eye_off", "lock"]],
    divertido: [["lock", "heart"], ["anonymous"], ["shield"]],
    familiar: [["lock", "family", "heart"], ["shield", "home"], ["secure", "family"]],
    premium: [["lock", "shield", "secure"], ["anonymous", "lock"], ["eye_off", "shield"]],
  },
};

// ============================================
// CHARACTER RECOMMENDATIONS BY TONE
// ============================================
const CHARACTER_RECOMMENDATIONS = {
  urgente: ["warning_face", "alert", null, "sos_emoji", null],
  divertido: ["car_happy", "dog", "cat", "star", "heart"],
  familiar: ["family", "heart", "home", "hug", "smile"],
  premium: [null, null, "shield", null, "star"],
};

// ============================================
// MAIN AI ENGINE CLASS
// ============================================
export class StickerAIEngine {
  constructor() {
    this.lastGeneration = null;
  }

  /**
   * Generate 5 design token variants
   * @param {Object} params - Generation parameters
   * @param {string} params.category - Category (vehiculos, ninos, mascotas, etc.)
   * @param {string} params.tone - Emotional tone (urgente, divertido, familiar, premium)
   * @param {string} params.country - Country code (PE, LATAM)
   * @param {number} params.urgency - Urgency level (1-5)
   * @returns {Array} - 5 design token objects
   */
  generateVariants({ category, tone, country = "PE", urgency = 3 }) {
    const normalizedCategory = this.normalizeCategory(category);
    const normalizedTone = this.normalizeTone(tone);
    
    const variants = [];
    
    // Get base data for this combination
    const palettes = TONE_PALETTES[normalizedTone] || TONE_PALETTES.familiar;
    const slogans = SLOGAN_TEMPLATES[normalizedCategory]?.[normalizedTone] || 
                    SLOGAN_TEMPLATES.vehiculos.familiar;
    const iconSets = ICON_RECOMMENDATIONS[normalizedCategory]?.[normalizedTone] || 
                     [["car", "phone"]];
    const characters = CHARACTER_RECOMMENDATIONS[normalizedTone] || [null];
    
    // Generate 5 unique variants
    for (let i = 0; i < 5; i++) {
      const palette = palettes[i % palettes.length];
      const slogan = slogans[i % slogans.length];
      const icons = iconSets[i % iconSets.length];
      const character = characters[i % characters.length];
      
      // Apply urgency modifier to colors
      const modifiedPalette = this.applyUrgencyModifier(palette, urgency);
      
      // Generate title based on category and tone
      const title = this.generateTitle(normalizedCategory, normalizedTone, i);
      
      // Generate subtitle
      const subtitle = this.generateSubtitle(normalizedTone, country);
      
      variants.push({
        id: `ai_variant_${i + 1}`,
        name: `Variante ${i + 1}: ${palette.name}`,
        designTokens: {
          // Background gradient
          backgroundGradient: {
            id: `ai_gradient_${i}`,
            name: palette.name,
            colors: [modifiedPalette.primary, modifiedPalette.secondary],
            angle: 135 + (i * 15), // Slight angle variation
          },
          // Text colors
          primaryTextColor: {
            id: "ai_primary",
            name: "AI Primary",
            value: modifiedPalette.accent,
          },
          secondaryTextColor: {
            id: "ai_secondary", 
            name: "AI Secondary",
            value: modifiedPalette.accent + "BB",
          },
          // Content
          title: title,
          subtitle: subtitle,
          slogan: slogan,
          // Icons
          activeIcons: icons,
          iconPosition: i % 2 === 0 ? "top-right" : "top-left",
          showIcons: icons.length > 0,
          // Character
          characterId: character,
          characterEnabled: character !== null,
          characterPosition: i % 2 === 0 ? "bottom-right" : "bottom-left",
          // Effects
          frameEnabled: true,
          glowEnabled: urgency >= 4 || normalizedTone === "urgente",
        },
        metadata: {
          category: normalizedCategory,
          tone: normalizedTone,
          urgency: urgency,
          country: country,
          emotionalScore: this.calculateEmotionalScore(normalizedTone, urgency),
        },
      });
    }
    
    this.lastGeneration = {
      timestamp: Date.now(),
      params: { category, tone, country, urgency },
      variants,
    };
    
    return variants;
  }

  /**
   * Normalize category name
   */
  normalizeCategory(category) {
    const mapping = {
      vehiculos: "vehiculos",
      vehicles: "vehiculos",
      autos: "vehiculos",
      ninos: "ninos",
      niños: "ninos",
      children: "ninos",
      kids: "ninos",
      mascotas: "mascotas",
      pets: "mascotas",
      objetos: "objetos",
      objects: "objetos",
      maletas: "objetos",
      viajes: "viajes",
      travel: "viajes",
      emergencias: "emergencias",
      emergency: "emergencias",
      privacidad: "privacidad",
      privacy: "privacidad",
    };
    return mapping[category?.toLowerCase()] || "vehiculos";
  }

  /**
   * Normalize tone name
   */
  normalizeTone(tone) {
    const mapping = {
      urgente: "urgente",
      urgent: "urgente",
      divertido: "divertido",
      fun: "divertido",
      funny: "divertido",
      familiar: "familiar",
      family: "familiar",
      premium: "premium",
      pro: "premium",
      professional: "premium",
    };
    return mapping[tone?.toLowerCase()] || "familiar";
  }

  /**
   * Apply urgency modifier to palette
   */
  applyUrgencyModifier(palette, urgency) {
    if (urgency <= 2) {
      // Low urgency - softer colors
      return palette;
    } else if (urgency >= 4) {
      // High urgency - more saturated
      return {
        ...palette,
        primary: this.saturateColor(palette.primary, 0.15),
        secondary: this.lightenColor(palette.secondary, 0.1),
      };
    }
    return palette;
  }

  /**
   * Saturate a hex color
   */
  saturateColor(hex, amount) {
    // Simple saturation boost
    return hex; // Keep original for now to avoid breaking colors
  }

  /**
   * Lighten a hex color
   */
  lightenColor(hex, amount) {
    if (!hex || hex.length < 7) return hex;
    try {
      const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + Math.floor(255 * amount));
      const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + Math.floor(255 * amount));
      const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + Math.floor(255 * amount));
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    } catch {
      return hex;
    }
  }

  /**
   * Generate title based on category and tone
   */
  generateTitle(category, tone, index) {
    const titles = {
      vehiculos: {
        urgente: ["¡Atención!", "¿Bloqueo?", "¡Urgente!", "Avisa Ya", "¡Alerta!"],
        divertido: ["¡Ups!", "Oops...", "¡Hey!", "Hola!", "¿Yo?"],
        familiar: ["Gracias", "Con cariño", "Ayúdame", "Por favor", "Tu ayuda"],
        premium: ["Contacto", "Privado", "Exclusivo", "Premium", "Discreto"],
      },
      ninos: {
        urgente: ["¡Ayuda!", "¿Me ves?", "Busco a mamá", "¡Urgente!", "Por favor"],
        divertido: ["¡Hola!", "Soy yo", "Explorador", "Aventura", "¿Me perdí?"],
        familiar: ["Mi familia", "Con amor", "Protegido", "Seguro", "Cuidado"],
        premium: ["Protección", "Seguridad", "Privado", "Exclusivo", "Elite"],
      },
      mascotas: {
        urgente: ["¡Ayuda!", "¿Me viste?", "Perdido", "¡Urgente!", "Busco hogar"],
        divertido: ["¡Guau!", "¡Miau!", "Patitas", "Hola humano", "¿Me llevas?"],
        familiar: ["Mi hogar", "Con amor", "Familia", "Protegido", "Querido"],
        premium: ["Protección", "Seguridad", "Privado", "Exclusivo", "Premium"],
      },
    };
    
    const categoryTitles = titles[category] || titles.vehiculos;
    const toneTitles = categoryTitles[tone] || categoryTitles.familiar;
    return toneTitles[index % toneTitles.length];
  }

  /**
   * Generate subtitle based on tone and country
   */
  generateSubtitle(tone, country) {
    const subtitles = {
      urgente: "Contacto inmediato",
      divertido: "Contacto fácil",
      familiar: "Contacto seguro",
      premium: "Contacto privado",
    };
    
    const countryPhrases = {
      PE: " - Perú",
      LATAM: " - LATAM",
    };
    
    return subtitles[tone] + (countryPhrases[country] || "");
  }

  /**
   * Calculate emotional score for the design
   */
  calculateEmotionalScore(tone, urgency) {
    const toneScores = {
      urgente: 0.9,
      divertido: 0.7,
      familiar: 0.8,
      premium: 0.85,
    };
    
    const base = toneScores[tone] || 0.5;
    const urgencyFactor = urgency / 5;
    
    return Math.min(1, base * (0.5 + urgencyFactor * 0.5));
  }

  /**
   * Get emotional trigger words for a tone
   */
  getEmotionalWords(tone) {
    return EMOTIONAL_WORDS[tone] || EMOTIONAL_WORDS.familiar;
  }

  /**
   * Validate slogan (max 45 chars)
   */
  validateSlogan(slogan) {
    if (!slogan) return { valid: false, message: "Slogan vacío" };
    if (slogan.length > 45) {
      return { 
        valid: false, 
        message: `Slogan muy largo (${slogan.length}/45)`,
        suggestion: slogan.slice(0, 42) + "..."
      };
    }
    return { valid: true, slogan };
  }

  /**
   * Get last generation for debugging
   */
  getLastGeneration() {
    return this.lastGeneration;
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================
const stickerAI = new StickerAIEngine();

// ============================================
// EXPORTED FUNCTIONS
// ============================================

/**
 * Generate AI design variants
 */
export const generateAIVariants = (params) => {
  return stickerAI.generateVariants(params);
};

/**
 * Get emotional words for a tone
 */
export const getEmotionalWords = (tone) => {
  return stickerAI.getEmotionalWords(tone);
};

/**
 * Validate a slogan
 */
export const validateSlogan = (slogan) => {
  return stickerAI.validateSlogan(slogan);
};

/**
 * Available tones
 */
export const AI_TONES = [
  { id: "urgente", name: "Urgente", icon: "🚨", description: "Llamada a la acción rápida" },
  { id: "divertido", name: "Divertido", icon: "😄", description: "Tono amigable y casual" },
  { id: "familiar", name: "Familiar", icon: "👨‍👩‍👧", description: "Cálido y cercano" },
  { id: "premium", name: "Premium", icon: "✨", description: "Elegante y profesional" },
];

/**
 * Available urgency levels
 */
export const AI_URGENCY_LEVELS = [
  { id: 1, name: "Muy bajo", description: "Sin prisa" },
  { id: 2, name: "Bajo", description: "Cuando puedas" },
  { id: 3, name: "Normal", description: "Pronto" },
  { id: 4, name: "Alto", description: "Importante" },
  { id: 5, name: "Muy alto", description: "¡Urgente!" },
];

export default {
  generateAIVariants,
  getEmotionalWords,
  validateSlogan,
  AI_TONES,
  AI_URGENCY_LEVELS,
  StickerAIEngine,
};
